import { useState, useCallback, useMemo } from 'react';
import base64js from 'base64-js';

const useUtmData = (paramNames = 'd') => {
    const [error, setError] = useState(null);

    const params = useMemo(() => {
        return Array.isArray(paramNames) ? paramNames : [paramNames];
    }, [paramNames]);

    const isMultiple = Array.isArray(paramNames);

    const encodeBase64 = (str) => {
        const bytes = new TextEncoder().encode(str);
        const base64 = base64js.fromByteArray(bytes);
        return base64;
    };

    const decodeBase64 = (base64) => {
        const bytes = base64js.toByteArray(base64);
        const str = new TextDecoder().decode(bytes);
        return str;
    };

    const isValidBase64 = (str) => {
        try {
            base64js.toByteArray(str);
            return true;
        } catch (err) {
            return false;
        }
    };

    const createSingleParamMethods = useCallback((paramName) => {
        const encodeToUrl = (data, replaceCurrentUrl = true) => {
            try {
                setError(null);

                if (data === null || data === undefined) {
                    throw new Error('Data cannot be null or undefined');
                }

                const jsonString = JSON.stringify(data);

                const base64String = encodeBase64(jsonString);

                const currentUrl = new URL(window.location);

                currentUrl.searchParams.set(paramName, base64String);

                if (replaceCurrentUrl) {
                    window.history.replaceState({}, '', currentUrl.toString());
                }

                return currentUrl.toString();
            } catch (err) {
                const errorMsg = `Encoding error: ${err.message}`;
                setError(errorMsg);
                console.error('[useUtmData] ERROR:', errorMsg, err);
                return null;
            }
        };

        const decodeFromUrl = (url = null) => {
            try {
                setError(null);

                const targetUrl = url ? new URL(url) : new URL(window.location);

                const paramValue = targetUrl.searchParams.get(paramName);

                if (!paramValue) {
                    return null;
                }

                if (!isValidBase64(paramValue)) {
                    throw new Error('Invalid base64 format');
                }

                const jsonString = decodeBase64(paramValue);

                const data = JSON.parse(jsonString);

                return data;
            } catch (err) {
                const errorMsg = `Decoding error: ${err.message}`;
                setError(errorMsg);
                console.error('[useUtmData] ERROR:', errorMsg, err);
                return null;
            }
        };

        const decodeCustomFormat = (...keyNames) => {
            try {
                setError(null);
                const targetUrl = new URL(window.location);
                const paramValue = targetUrl.searchParams.get(paramName);

                if (!paramValue) {
                    return null;
                }

                if (!isValidBase64(paramValue)) {
                    throw new Error('Invalid base64 format');
                }

                const decodedString = decodeBase64(paramValue);
                const parts = decodedString.split('§');

                if (keyNames.length === 0) {
                    return parts;
                }

                const result = {};
                keyNames.forEach((key, index) => {
                    result[key] = parts[index] || null;
                });

                return result;
            } catch (err) {
                const errorMsg = `Custom decoding error: ${err.message}`;
                setError(errorMsg);
                console.error('[useUtmData] ERROR:', errorMsg, err);
                return null;
            }
        };

        const getValue = (key, url = null) => {
            const data = decodeFromUrl(url);
            const value = data && typeof data === 'object' ? data[key] : null;

            return value;
        };

        const removeFromUrl = () => {
            try {
                const currentUrl = new URL(window.location);
                currentUrl.searchParams.delete(paramName);
                window.history.replaceState({}, '', currentUrl.toString());

                return true;
            } catch (err) {
                const errorMsg = `Delete error: ${err.message}`;
                setError(errorMsg);
                console.error('[useUtmData] ERROR:', errorMsg, err);
                return false;
            }
        };

        const hasParam = (url = null) => {
            try {
                const targetUrl = url ? new URL(url) : new URL(window.location);
                const exists = targetUrl.searchParams.has(paramName);

                return exists;
            } catch (err) {
                console.error('[useUtmData] ERROR checking parameter existence:', err);
                return false;
            }
        };

        return {
            encodeToUrl,
            decodeFromUrl,
            decodeCustomFormat,
            getValue,
            removeFromUrl,
            hasParam
        };
    }, []);

    const result = useMemo(() => {
        if (!isMultiple) {
            return {
                ...createSingleParamMethods(params[0]),
                error
            };
        } else {
            const methods = {};
            params.forEach(param => {
                methods[param] = createSingleParamMethods(param);
            });
            return {
                ...methods,
                error
            };
        }
    }, [params, isMultiple, createSingleParamMethods, error]);

    return result;
};

export default useUtmData;